import { Link } from 'react-router-dom';
import {
  addMoney, Banner, ButtonLink, compareMoney, DataTable, formatMoney, isZero, Money, negate,
  PageHeader, Panel, Pill, StatTile, type LinkComponent, type Tone,
} from '@elysian/ui';
import type { AccountBalance, AgedBalance, BankAccountSummary, Dashboard } from '../data/source';
import { useAsync } from '../data/useAsync';
import { useSession } from '../state/session';

const RouterLink = Link as unknown as LinkComponent;
const BUCKETS = [
  { key: 'current', label: 'Not yet due' },
  { key: 'd30', label: '1–30 days', tone: 'warn' },
  { key: 'd60', label: '31–60 days', tone: 'danger' },
  { key: 'd90', label: '61–90 days', tone: 'danger' },
  { key: 'older', label: 'Over 90', tone: 'danger' },
] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const longDate = (d: Date) =>
  new Intl.DateTimeFormat('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(d);
const shortDate = (s: string) =>
  new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'long' }).format(new Date(`${s}T12:00:00`));

/** The oldest bucket with money in it decides the pill. */
export function oldestPill(row: AgedBalance): { tone: Tone; text: string } {
  if (compareMoney(row.older, '0') > 0) return { tone: 'danger', text: 'Over 90 days' };
  if (compareMoney(row.d90, '0') > 0) return { tone: 'danger', text: '61–90 days' };
  if (compareMoney(row.d60, '0') > 0) return { tone: 'danger', text: '31–60 days' };
  if (compareMoney(row.d30, '0') > 0) return { tone: 'warn', text: '1–30 days' };
  return { tone: 'ok', text: 'Not due' };
}

/**
 * Income, cost of sales (5xxx), overheads and net profit from P&L lines.
 * The report gives income and expenses both as positive figures.
 */
export function summarisePnl(lines: AccountBalance[]) {
  const sum = (f: (l: AccountBalance) => boolean) => addMoney('0', ...lines.filter(f).map((l) => l.balance));
  const income = sum((l) => l.class === 'income');
  const costOfSales = sum((l) => l.class === 'expense' && (l.code ?? '').startsWith('5'));
  const overheads = sum((l) => l.class === 'expense' && !(l.code ?? '').startsWith('5'));
  const gross = addMoney(income, negate(costOfSales));
  return { income, costOfSales, gross, overheads, net: addMoney(gross, negate(overheads)) };
}

interface Loaded { dash: Dashboard; aged: AgedBalance[]; banks: BankAccountSummary[]; pnl: AccountBalance[] }

export function Overview() {
  const { source, context, today } = useSession();
  const now = today();
  const currency = context?.organisation.base_currency ?? 'EUR';
  const basis = context?.organisation.vat_basis === 'cash' ? 'cash basis' : 'invoice basis';
  const yearStart = `${now.getFullYear()}-01-01`;

  const data = useAsync<Loaded>(async () => {
    const [dash, aged, banks, pnl] = await Promise.all([
      source.dashboard(), source.agedDebtors(iso(now)), source.bankAccounts(), source.profitAndLoss(yearStart, iso(now)),
    ]);
    return { dash, aged, banks, pnl };
  }, [source]);

  const ready = data.state === 'ready' ? data.data : null;
  const bankTodo = ready?.dash.to_do.find((t) => t.kind === 'bank_lines')?.count ?? 0;

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={`${longDate(now)} · figures in ${currency}, ${basis}`}
        actions={
          <>
            <ButtonLink as={RouterLink} to="/inbox">Record a bill</ButtonLink>
            <ButtonLink as={RouterLink} to="/invoices/new" variant="primary">New invoice</ButtonLink>
          </>
        }
      />

      {data.state === 'error' && (
        <Banner>Couldn't load your figures: {data.error.message}. Refresh to try again.</Banner>
      )}

      {ready && (
        <Banner action={bankTodo > 0 ? <Link to="/bank">Reconcile now →</Link> : undefined}>
          <strong style={{ fontWeight: 600 }}>
            VAT for {ready.dash.vat_due.period_label} is due {shortDate(ready.dash.vat_due.due_date)}.
          </strong>{' '}
          {bankTodo > 0
            ? `${bankTodo} bank transactions still need reconciling before you can file.`
            : 'Everything is reconciled, so you can file when ready.'}
        </Banner>
      )}

      <div className="el-grid" data-min="220">
        <StatTile label="Cash in bank" value={ready?.dash.cash} currency={currency}
          note={ready && `${ready.banks.length} accounts`} />
        <StatTile label="Owed to you" value={ready?.dash.owed_to_you} currency={currency}
          note={ready?.dash.overdue_to_you && !isZero(ready.dash.overdue_to_you)
            ? <Pill tone="danger">{formatMoney(ready.dash.overdue_to_you, currency)} overdue</Pill>
            : undefined} />
        <StatTile label="You owe" value={ready?.dash.you_owe} currency={currency} />
        <StatTile label={`VAT payable${ready ? ` · ${ready.dash.vat_due.period_label} so far` : ''}`}
          value={ready?.dash.vat_due.amount} currency={currency} />
      </div>

      {ready && <OverviewDetail data={ready} currency={currency} />}
    </>
  );
}

function OverviewDetail({ data, currency }: { data: Loaded; currency: string }) {
  const totals = Object.fromEntries(
    BUCKETS.map((b) => [b.key, addMoney('0', ...data.aged.map((r) => r[b.key]))]),
  ) as Record<(typeof BUCKETS)[number]['key'], string>;
  const sorted = [...data.aged].sort((a, b) => compareMoney(b.total, a.total));
  const top = sorted.slice(0, 4);
  const rest = sorted.slice(4);
  const pnl = summarisePnl(data.pnl);

  return (
    <>
      <div className="el-grid" data-min="380">
        <Panel id="owed-h" title="Money owed to you" flush action={<Link to="/reports/aged-debtors" style={{ fontSize: 14 }}>Aged debtors →</Link>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', margin: '0 20px 16px', border: '1px solid var(--el-rule)', borderRadius: 10, overflow: 'hidden' }}>
            {BUCKETS.map((b, i) => (
              <div key={b.key} style={{ padding: '10px 12px', background: i === 0 ? 'var(--el-surface-soft)' : undefined, boxShadow: i ? '-1px 0 0 var(--el-rule)' : undefined }}>
                <div style={{ fontSize: 12, color: 'var(--el-muted)' }}>{b.label}</div>
                <div style={{ fontWeight: 600, marginTop: 2 }}>
                  <Money value={totals[b.key]} currency={currency}
                    tone={'tone' in b && compareMoney(totals[b.key], '0') > 0 ? b.tone : undefined} />
                </div>
              </div>
            ))}
          </div>
          <DataTable
            caption="Customers with the largest balances"
            rows={top}
            rowKey={(r) => r.contact_id}
            empty="No customers owe you money."
            columns={[
              { key: 'n', header: 'Customer', render: (r) => r.contact_name },
              { key: 'o', header: 'Oldest', render: (r) => { const p = oldestPill(r); return <Pill tone={p.tone}>{p.text}</Pill>; } },
              { key: 't', header: 'Outstanding', numeric: true, render: (r) => formatMoney(r.total, currency) },
            ]}
          />
          {rest.length > 0 && (
            <p className="el-muted" style={{ margin: 0, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--el-rule-soft)', fontSize: 14 }}>
              <span>+ {rest.length} more</span>
              <span className="el-num">{formatMoney(addMoney('0', ...rest.map((r) => r.total)), currency)}</span>
            </p>
          )}
        </Panel>

        <Panel id="todo-h" title="Needs your attention">
          <div>
            {data.dash.to_do.filter((t) => t.count > 0).map((t) => (
              <Link key={t.kind} to={TODO_ROUTE[t.kind]} className="el-todo">
                <span className="el-todo-icon" data-tone={TODO_TONE[t.kind]} aria-hidden="true">{TODO_ICON[t.kind]}</span>
                <span style={{ flex: 1, fontWeight: 600 }}>{t.count} {t.label}</span>
                <span style={{ color: 'var(--el-brand)' }} aria-hidden="true">→</span>
              </Link>
            ))}
            {data.dash.to_do.every((t) => t.count === 0) && <p className="el-muted">Nothing needs you right now.</p>}
          </div>
        </Panel>
      </div>

      <div className="el-grid" data-min="380">
        <Panel id="pl-h" title="Profit and loss · year to date" flush action={<Link to="/reports" style={{ fontSize: 14 }}>Full report →</Link>}>
          <DataTable
            caption="Profit and loss summary"
            hideHeader
            rowKey={(r) => r.label}
            rows={[
              { label: 'Income', value: pnl.income },
              { label: 'Cost of sales', value: negate(pnl.costOfSales) },
              { label: 'Gross profit', value: pnl.gross, strong: true },
              { label: 'Overheads', value: negate(pnl.overheads) },
              { label: 'Net profit', value: pnl.net, strong: true, net: true },
            ]}
            columns={[
              { key: 'l', header: 'Line', render: (r) => (r.strong ? <strong>{r.label}</strong> : r.label) },
              { key: 'v', header: 'Amount', numeric: true, render: (r) => (
                <span style={r.strong ? { fontWeight: 600 } : undefined}>
                  <Money value={r.value} currency={currency} tone={r.net && compareMoney(r.value, '0') > 0 ? 'positive' : undefined} />
                </span>
              ) },
            ]}
          />
        </Panel>
        <Panel id="bank-h" title="Bank accounts" flush action={<Link to="/bank" style={{ fontSize: 14 }}>Reconcile →</Link>}>
          <DataTable
            caption="Bank accounts"
            rows={data.banks}
            rowKey={(b) => b.code}
            columns={[
              { key: 'n', header: 'Account', render: (b) => b.name },
              { key: 's', header: 'Status', render: (b) => b.unmatched_count > 0
                ? <Pill tone="warn">{b.unmatched_count} to reconcile</Pill>
                : <Pill>Reconciled</Pill> },
              { key: 'b', header: 'Balance', numeric: true, render: (b) => formatMoney(b.ledger_balance, b.currency) },
            ]}
          />
        </Panel>
      </div>
    </>
  );
}

type TodoKind = Dashboard['to_do'][number]['kind'];
const TODO_ROUTE: Record<TodoKind, string> = {
  bank_lines: '/bank', inbox: '/inbox', recurring: '/recurring', overdue: '/invoices', vat: '/vat',
};
const TODO_TONE: Record<TodoKind, Tone> = {
  bank_lines: 'warn', inbox: 'info', recurring: 'ok', overdue: 'ok', vat: 'warn',
};
const icon = (d: string, stroke: string) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d={d} stroke={stroke} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const TODO_ICON: Record<TodoKind, JSX.Element> = {
  bank_lines: icon('M2 6h12M2 10h12M5 3l-3 3 3 3M11 7l3 3-3 3', '#854F0B'),
  inbox: icon('M4 2h6l3 3v9H4zM6.5 9.5l1.5 1.5 2.5-3', '#3C3489'),
  recurring: icon('M3 8a5 5 0 019-3M13 8a5 5 0 01-9 3M12 2v3H9M4 14v-3h3', '#0F6E56'),
  overdue: icon('M4 2h8v12H4zM6 6h4M6 9h4', '#0F6E56'),
  vat: icon('M8 2L14 13H2L8 2ZM8 6.5v3', '#854F0B'),
};
